import React, { useEffect, useState, useCallback } from "react";
import { api, formatApiErrorDetail } from "@/lib/api";
import { AdminLayout } from "@/pages/admin/AdminLayout";
import { toast } from "sonner";
import { Search, Plus, X, RefreshCw, Tag, Users, Copy, AlertTriangle, Link2, Trash2, GraduationCap, ShieldCheck, FileText } from "lucide-react";

const STAGES = ["lead", "applicant", "parent", "child", "student", "customer", "alumni", "educator", "partner", "investor"];
const STAGE_COLORS = {
  lead: "bg-slate-500", applicant: "bg-blue-500", parent: "bg-teal-500", child: "bg-cyan-500",
  student: "bg-emerald-500", customer: "bg-amber-500", alumni: "bg-purple-500",
  educator: "bg-pink-500", partner: "bg-orange-500", investor: "bg-heraldic",
};
const FLD = "mt-1 w-full bg-white/5 border border-white/10 rounded-sm px-2 py-1.5 text-sm text-paper";

function RelationLinker({ cid, onLinked }) {
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [rel, setRel] = useState("child");
  useEffect(() => {
    if (!q.trim()) { setResults([]); return; }
    const t = setTimeout(() => api.get(`/admin/contacts?q=${encodeURIComponent(q)}&limit=6`).then((r) => setResults(r.data.items.filter((x) => x.cid !== cid))), 250);
    return () => clearTimeout(t);
  }, [q, cid]);
  const link = async (other) => {
    try { await api.post(`/admin/contacts/${cid}/relationship`, { other_cid: other.cid, relation: rel }); toast.success(`Linked ${other.name || other.email} as ${rel}`); setQ(""); setResults([]); onLinked(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  return (
    <div className="space-y-2" data-testid="relation-linker">
      <div className="flex gap-2">
        <select value={rel} onChange={(e) => setRel(e.target.value)} className="bg-white/5 border border-white/10 rounded-sm px-2 py-1.5 text-sm" data-testid="relation-type">
          <option value="child">is parent of →</option>
          <option value="parent">is child of →</option>
        </select>
        <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search a contact to link…" className="flex-1 bg-white/5 border border-white/10 rounded-sm px-2 py-1.5 text-sm" data-testid="relation-search" />
      </div>
      {results.length > 0 && (
        <div className="border border-white/10 rounded-sm divide-y divide-white/5">
          {results.map((r) => <button key={r.cid} onClick={() => link(r)} className="w-full text-left px-3 py-2 text-sm hover:bg-white/5 flex justify-between"><span>{r.name || "—"}</span><span className="text-paper/40 text-xs">{r.email}</span></button>)}
        </div>
      )}
    </div>
  );
}

function Detail({ cid, onClose, onChanged }) {
  const [c, setC] = useState(null);
  const [note, setNote] = useState("");
  const load = useCallback(() => { api.get(`/admin/contacts/${cid}`).then((r) => setC(r.data)); }, [cid]);
  useEffect(() => { load(); }, [load]);
  if (!c) return null;
  const set = (k, v) => setC({ ...c, [k]: v });
  const save = async () => {
    try { await api.put(`/admin/contacts/${cid}`, c); toast.success("Saved"); onChanged(); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };
  const addNote = async () => { if (!note.trim()) return; await api.post(`/admin/contacts/${cid}/note`, { text: note }); setNote(""); load(); };
  const unlink = async (other) => { await api.delete(`/admin/contacts/${cid}/relationship/${other}`); load(); onChanged(); };
  return (
    <div className="fixed inset-0 z-50 flex justify-end" data-testid="contact-detail">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative w-full max-w-lg bg-[#0B0D12] border-l border-white/10 h-full overflow-auto">
        <div className="flex items-center justify-between px-6 h-16 border-b border-white/10 sticky top-0 bg-[#0B0D12] z-10">
          <div className="font-serif text-xl">{c.name || c.email}</div>
          <button onClick={onClose} data-testid="contact-detail-close"><X className="h-5 w-5 text-paper/50" /></button>
        </div>
        <div className="p-6 space-y-5">
          <div className="grid grid-cols-2 gap-3">
            <label className="text-xs text-paper/50">First name<input value={c.first_name || ""} onChange={(e) => set("first_name", e.target.value)} className={FLD} data-testid="contact-field-first_name" /></label>
            <label className="text-xs text-paper/50">Last name<input value={c.last_name || ""} onChange={(e) => set("last_name", e.target.value)} className={FLD} data-testid="contact-field-last_name" /></label>
            <label className="text-xs text-paper/50">Full name<input value={c.name || ""} onChange={(e) => set("name", e.target.value)} className={FLD} data-testid="contact-field-name" /></label>
            <label className="text-xs text-paper/50">Email<input value={c.email || ""} onChange={(e) => set("email", e.target.value)} className={FLD} data-testid="contact-field-email" /></label>
            <label className="text-xs text-paper/50">Phone<input value={c.phone || ""} onChange={(e) => set("phone", e.target.value)} className={FLD} data-testid="contact-field-phone" /></label>
            <label className="text-xs text-paper/50">Country<input value={c.country || ""} onChange={(e) => set("country", e.target.value)} className={FLD} /></label>
            <label className="text-xs text-paper/50">Role / stage
              <select value={c.lifecycle_stage} onChange={(e) => set("lifecycle_stage", e.target.value)} className={`${FLD} capitalize`} data-testid="contact-field-stage">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </label>
            <label className="text-xs text-paper/50">Preferred language
              <select value={c.preferred_language || ""} onChange={(e) => set("preferred_language", e.target.value)} className={FLD} data-testid="contact-field-lang">
                <option value="">—</option><option value="fr">Français</option><option value="en">English</option><option value="es">Español</option>
              </select>
            </label>
          </div>
          <label className="text-xs text-paper/50 block">Tags (comma separated)
            <input value={(c.tags || []).join(", ")} onChange={(e) => set("tags", e.target.value.split(",").map((t) => t.trim()).filter(Boolean))} className={FLD} data-testid="contact-field-tags" />
          </label>
          <div className="flex items-center gap-4 text-xs">
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!c.consent} onChange={(e) => set("consent", e.target.checked)} data-testid="contact-consent" /> Marketing consent</label>
            <label className="flex items-center gap-2"><input type="checkbox" checked={!!c.unsubscribed} onChange={(e) => set("unsubscribed", e.target.checked)} /> Unsubscribed</label>
          </div>
          <button onClick={save} data-testid="contact-save" className="bg-heraldic text-white px-4 py-2 rounded-sm text-sm">Save contact</button>
          {c.lifecycle_stage === "student" && <a href={`/admin/sitf?student=${cid}`} data-testid="contact-trajectory-link" className="ml-2 inline-flex items-center gap-1.5 border border-heraldic/40 text-heraldic px-4 py-2 rounded-sm text-sm"><GraduationCap className="h-4 w-4" /> Intellectual Trajectory</a>}

          {(() => {
            const pairs = (c.form_fields && c.form_fields.length)
              ? c.form_fields
              : Object.entries(c.form_answers || {}).map(([k, v]) => ({ q: k.replace(/_/g, " "), a: v }));
            const shown = pairs.filter((p) => p.a);
            if (!shown.length && c.source !== "meta") return null;
            return (
              <div className="border-t border-white/10 pt-4" data-testid="contact-form-submission">
                <div className="text-xs uppercase text-paper/40 mb-2 flex items-center gap-1.5"><FileText className="h-3.5 w-3.5" /> Lead form submission</div>
                <div className="flex flex-wrap gap-1.5 mb-3 text-[11px]">
                  {c.source && <span className="bg-heraldic/15 text-heraldic px-2 py-0.5 rounded-sm">source: {c.source}</span>}
                  {c.funnel && <span className="bg-white/5 text-paper/70 px-2 py-0.5 rounded-sm">funnel: {c.funnel}</span>}
                  {c.form_name && <span className="bg-white/5 text-paper/70 px-2 py-0.5 rounded-sm">{c.form_name}</span>}
                  {c.market && <span className="bg-white/5 text-paper/70 px-2 py-0.5 rounded-sm">market: {c.market}</span>}
                </div>
                {shown.length ? (
                  <dl className="space-y-1.5">
                    {shown.map((p, i) => (
                      <div key={i} className="bg-white/5 rounded-sm px-3 py-2">
                        <dt className="text-[11px] text-paper/40 capitalize">{p.q}</dt>
                        <dd className="text-sm text-paper/85 break-words">{p.a}</dd>
                      </div>
                    ))}
                  </dl>
                ) : <div className="text-paper/40 text-sm">No form answers captured.</div>}
              </div>
            );
          })()}

          <div className="border-t border-white/10 pt-4">
            <div className="text-xs uppercase text-paper/40 mb-2 flex items-center gap-1.5"><Link2 className="h-3.5 w-3.5" /> Family / relationships</div>
            <div className="space-y-1.5 mb-3" data-testid="relationships-list">
              {(c.relationships || []).map((r) => (
                <div key={r.cid} className="flex items-center justify-between bg-white/5 rounded-sm px-3 py-2 text-sm">
                  <span><span className="text-heraldic capitalize">{r.type}</span> · {r.name}</span>
                  <button onClick={() => unlink(r.cid)} className="text-red-400/70 hover:text-red-400"><Trash2 className="h-3.5 w-3.5" /></button>
                </div>
              ))}
              {(c.relationships || []).length === 0 && <div className="text-paper/40 text-sm">No linked family members yet.</div>}
            </div>
            <RelationLinker cid={cid} onLinked={() => { load(); onChanged(); }} />
          </div>

          <div className="border-t border-white/10 pt-4">
            <div className="text-xs uppercase text-paper/40 mb-2">Notes</div>
            <div className="flex gap-2"><input value={note} onChange={(e) => setNote(e.target.value)} placeholder="Add a note…" className="flex-1 bg-white/5 border border-white/10 rounded-sm px-2 py-1.5 text-sm" data-testid="contact-note-input" /><button onClick={addNote} data-testid="contact-note-add" className="border border-white/15 px-3 rounded-sm text-sm">Add</button></div>
            <div className="mt-3 space-y-1.5">{(c.notes || []).slice().reverse().map((n, i) => <div key={i} className="text-sm bg-white/5 rounded-sm px-3 py-2"><span className="text-paper/80">{n.text}</span><div className="text-[10px] text-paper/40">{n.by} · {n.ts?.slice(0, 10)}</div></div>)}</div>
          </div>
        </div>
      </div>
    </div>
  );
}

function DuplicatesModal({ onClose, onOpen }) {
  const [groups, setGroups] = useState([]);
  const [total, setTotal] = useState(0);
  useEffect(() => { api.get("/admin/contact-duplicates").then((r) => { setGroups(r.data.groups); setTotal(r.data.total_groups); }); }, []);
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="dupes-modal">
      <div className="absolute inset-0 bg-black/60" onClick={onClose} />
      <div className="relative bg-[#0B0D12] border border-white/10 rounded-md p-6 w-full max-w-2xl max-h-[80vh] overflow-auto">
        <div className="flex items-center justify-between mb-4"><div className="font-serif text-xl flex items-center gap-2"><AlertTriangle className="h-5 w-5 text-amber-400" /> Possible duplicates ({total} groups)</div><button onClick={onClose}><X className="h-5 w-5 text-paper/50" /></button></div>
        <div className="space-y-3">
          {groups.map((g) => (
            <div key={g.key} className="border border-white/10 rounded-sm p-3">
              <div className="text-xs text-paper/40 mb-1">Name: “{g.key}” · {g.contacts.length} records</div>
              {g.contacts.map((c) => <button key={c.cid} onClick={() => onOpen(c.cid)} className="w-full text-left flex justify-between px-2 py-1.5 text-sm hover:bg-white/5 rounded-sm"><span>{c.name}</span><span className="text-paper/40 text-xs">{c.email} · {c.lifecycle_stage}</span></button>)}
            </div>
          ))}
          {groups.length === 0 && <div className="text-paper/40 text-sm py-6 text-center">No duplicates detected.</div>}
        </div>
      </div>
    </div>
  );
}

export default function AdminContacts() {
  const [data, setData] = useState({ items: [], total: 0, has_more: false });
  const [meta, setMeta] = useState(null);
  const [q, setQ] = useState("");
  const [stage, setStage] = useState("");
  const [tag, setTag] = useState("");
  const [missing, setMissing] = useState("");
  const [limit, setLimit] = useState(200);
  const [active, setActive] = useState(null);
  const [showNew, setShowNew] = useState(false);
  const [showDupes, setShowDupes] = useState(false);
  const [nc, setNc] = useState({ name: "", email: "", lifecycle_stage: "lead" });
  const [selected, setSelected] = useState({});
  const [selectAll, setSelectAll] = useState(false);
  const [bulk, setBulk] = useState({ add_tags: "", remove_tags: "", set_consent: "", set_stage: "" });

  const load = useCallback(() => {
    const params = new URLSearchParams();
    if (q) params.set("q", q);
    if (stage) params.set("stage", stage);
    if (tag) params.set("tag", tag);
    if (missing) params.set("missing", missing);
    params.set("limit", String(limit));
    api.get(`/admin/contacts?${params}`).then((r) => setData(r.data));
    api.get("/admin/contacts/meta").then((r) => setMeta(r.data));
  }, [q, stage, tag, missing, limit]);
  useEffect(() => { const t = setTimeout(load, 250); return () => clearTimeout(t); }, [load]);
  useEffect(() => { setSelected({}); setSelectAll(false); }, [q, stage, tag, missing]);

  const selCount = selectAll ? data.total : Object.keys(selected).length;
  const toggleRow = (cid) => setSelected((s) => { const n = { ...s }; if (n[cid]) delete n[cid]; else n[cid] = true; return n; });

  const createContact = async () => {
    if (!nc.email && !nc.name) return toast.error("Name or email required");
    try { await api.post("/admin/contacts", nc); toast.success("Contact created"); setShowNew(false); setNc({ name: "", email: "", lifecycle_stage: "lead" }); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const applyBulk = async () => {
    const payload = {};
    if (selectAll) payload.filter = { q, stage, tag, missing };
    else payload.cids = Object.keys(selected);
    if (!selectAll && payload.cids.length === 0) return toast.error("Select contacts first");
    const add = bulk.add_tags.split(",").map((t) => t.trim()).filter(Boolean);
    const rem = bulk.remove_tags.split(",").map((t) => t.trim()).filter(Boolean);
    if (add.length) payload.add_tags = add;
    if (rem.length) payload.remove_tags = rem;
    if (bulk.set_consent) payload.set_consent = bulk.set_consent === "yes";
    if (bulk.set_stage) payload.set_stage = bulk.set_stage;
    if (!payload.add_tags && !payload.remove_tags && payload.set_consent === undefined && !payload.set_stage) return toast.error("Choose a bulk action");
    try { const r = await api.post("/admin/contacts/bulk", payload); toast.success(`Updated ${r.data.modified} of ${r.data.matched} contacts`); setBulk({ add_tags: "", remove_tags: "", set_consent: "", set_stage: "" }); setSelected({}); setSelectAll(false); load(); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const sendActivation = async () => {
    const payload = selectAll ? { filter: { q, stage, tag } } : { cids: Object.keys(selected) };
    if (!selectAll && (!payload.cids || payload.cids.length === 0)) return toast.error("Select contacts first");
    if (!window.confirm(`Send a secure Parent Portal activation link to ${selCount} contact(s)? Existing accounts are reused — no duplicates are created.`)) return;
    try { const r = await api.post("/admin/portal/send-activation", payload); toast.success(`Activation emails sent: ${r.data.sent} · skipped: ${r.data.skipped}`); }
    catch (e) { toast.error(formatApiErrorDetail(e.response?.data?.detail)); }
  };

  const tagList = meta ? Object.entries(meta.tags || {}).sort((a, b) => b[1] - a[1]) : [];

  return (
    <AdminLayout>
      <div className="p-8 max-w-7xl" data-testid="admin-contacts-page">
        <div className="flex items-center justify-between flex-wrap gap-3">
          <div><h1 className="font-serif text-3xl">Contacts & CRM</h1><p className="text-paper/50 text-sm mt-1">{data.total} {data.total === 1 ? "contact" : "contacts"} · edit, tag, link families and segment for campaigns.</p></div>
          <div className="flex gap-2">
            <button onClick={() => setShowDupes(true)} data-testid="contacts-dupes" className="inline-flex items-center gap-1.5 border border-white/15 px-3 py-2 rounded-sm text-sm hover:bg-white/5"><Copy className="h-4 w-4" /> Duplicates</button>
            <button onClick={() => setShowNew(true)} data-testid="contacts-add" className="inline-flex items-center gap-1.5 bg-heraldic text-white px-3 py-2 rounded-sm text-sm"><Plus className="h-4 w-4" /> New contact</button>
          </div>
        </div>

        {meta && (
          <div className="mt-5 flex flex-wrap gap-2" data-testid="contacts-stage-filters">
            <button onClick={() => setStage("")} className={`text-xs px-3 py-1.5 rounded-full border ${!stage ? "bg-heraldic text-white border-heraldic" : "border-white/15 text-paper/60"}`}>All ({meta.total})</button>
            {meta.stages.filter((s) => meta.by_stage[s]).map((s) => (
              <button key={s} onClick={() => setStage(s === stage ? "" : s)} className={`text-xs px-3 py-1.5 rounded-full border capitalize ${stage === s ? "bg-heraldic text-white border-heraldic" : "border-white/15 text-paper/60"}`}>{s} ({meta.by_stage[s]})</button>
            ))}
          </div>
        )}

        <div className="mt-4 flex flex-wrap items-center gap-2">
          <div className="relative flex-1 min-w-[220px] max-w-md">
            <Search className="h-4 w-4 absolute left-3 top-1/2 -translate-y-1/2 text-paper/40" />
            <input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name, email, phone…" data-testid="contacts-search" className="w-full bg-white/5 border border-white/10 rounded-sm pl-9 pr-3 py-2 text-sm" />
          </div>
          <select value={tag} onChange={(e) => setTag(e.target.value)} data-testid="contacts-tag-filter" className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm">
            <option value="">All tags</option>
            {tagList.map(([t, n]) => <option key={t} value={t}>{t} ({n})</option>)}
          </select>
          <select value={missing} onChange={(e) => setMissing(e.target.value)} data-testid="contacts-missing-filter" className="bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm">
            <option value="">All records</option>
            <option value="any">Missing any info</option>
            <option value="name">Missing name</option>
            <option value="email">Missing email</option>
            <option value="phone">Missing phone</option>
          </select>
        </div>

        {selCount > 0 && (
          <div className="mt-4 border border-heraldic/40 bg-heraldic/10 rounded-sm p-3 flex flex-wrap items-end gap-3" data-testid="bulk-bar">
            <div className="text-sm font-medium">{selCount} selected</div>
            <label className="text-xs text-paper/60">Add tags<input value={bulk.add_tags} onChange={(e) => setBulk({ ...bulk, add_tags: e.target.value })} placeholder="new-tag, Founding Family" list="taglist" className="mt-1 block bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm w-56" data-testid="bulk-add-tags" /></label>
            <label className="text-xs text-paper/60">Remove tags<input value={bulk.remove_tags} onChange={(e) => setBulk({ ...bulk, remove_tags: e.target.value })} placeholder="tag" list="taglist" className="mt-1 block bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm w-40" data-testid="bulk-remove-tags" /></label>
            <label className="text-xs text-paper/60">Consent<select value={bulk.set_consent} onChange={(e) => setBulk({ ...bulk, set_consent: e.target.value })} className="mt-1 block bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm" data-testid="bulk-consent"><option value="">—</option><option value="yes">Grant</option><option value="no">Revoke</option></select></label>
            <label className="text-xs text-paper/60">Set role<select value={bulk.set_stage} onChange={(e) => setBulk({ ...bulk, set_stage: e.target.value })} className="mt-1 block bg-[#0B0D12] border border-white/10 rounded-sm px-2 py-1.5 text-sm capitalize" data-testid="bulk-stage"><option value="">—</option>{STAGES.map((s) => <option key={s} value={s}>{s}</option>)}</select></label>
            <button onClick={applyBulk} data-testid="bulk-apply" className="bg-heraldic text-white px-4 py-2 rounded-sm text-sm">Apply</button>
            <button onClick={sendActivation} data-testid="bulk-send-activation" className="bg-emerald-600 text-white px-4 py-2 rounded-sm text-sm inline-flex items-center gap-1.5"><ShieldCheck className="h-4 w-4" /> Send Portal Activation</button>
            <div className="w-full text-[11px] text-paper/40">Tip: type any new tag name to create it. To make these contacts students, set <b>Set role → student</b>, then link their parents from each contact.</div>
            <datalist id="taglist">{tagList.map(([t]) => <option key={t} value={t} />)}</datalist>
          </div>
        )}

        {!selectAll && data.total > 0 && (
          <div className="mt-3 text-xs text-paper/50">Showing {data.items.length} of {data.total}. <button onClick={() => setSelectAll(true)} data-testid="select-all-matching" className="text-heraldic underline">Select all {data.total} matching</button></div>
        )}
        {selectAll && <div className="mt-3 text-xs text-heraldic">All {data.total} matching contacts selected. <button onClick={() => setSelectAll(false)} className="underline">Clear</button></div>}

        <div className="mt-4 border border-white/10 rounded-sm overflow-hidden">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-paper/50 text-xs uppercase"><tr>
              <th className="px-3 py-3 w-8"></th>
              <th className="text-left px-4 py-3">Name</th><th className="text-left px-4 py-3">Email</th>
              <th className="text-left px-4 py-3">Role</th><th className="text-left px-4 py-3">Tags</th></tr></thead>
            <tbody>
              {data.items.map((c) => (
                <tr key={c.cid} data-testid={`contact-row-${c.cid}`} className="border-t border-white/5 hover:bg-white/5">
                  <td className="px-3 py-3" onClick={(e) => e.stopPropagation()}><input type="checkbox" checked={selectAll || !!selected[c.cid]} disabled={selectAll} onChange={() => toggleRow(c.cid)} data-testid={`contact-check-${c.cid}`} /></td>
                  <td className="px-4 py-3 font-medium cursor-pointer" onClick={() => setActive(c.cid)}>{c.name || <span className="text-amber-400/80">— no name</span>}{(c.relationships || []).length > 0 && <GraduationCap className="inline h-3.5 w-3.5 ml-1.5 text-heraldic" />}</td>
                  <td className="px-4 py-3 text-paper/60 cursor-pointer" onClick={() => setActive(c.cid)}>{c.email || <span className="text-amber-400/80">—</span>}</td>
                  <td className="px-4 py-3"><span className="inline-flex items-center gap-1.5 text-xs capitalize"><span className={`h-2 w-2 rounded-full ${STAGE_COLORS[c.lifecycle_stage] || "bg-slate-500"}`} />{c.lifecycle_stage}</span></td>
                  <td className="px-4 py-3 text-paper/50">{(c.tags || []).slice(0, 3).join(", ")}</td>
                </tr>
              ))}
              {data.items.length === 0 && <tr><td colSpan={5} className="px-4 py-10 text-center text-paper/40">No contacts match.</td></tr>}
            </tbody>
          </table>
        </div>
        {data.has_more && <div className="mt-3 text-center"><button onClick={() => setLimit((l) => l + 200)} data-testid="contacts-load-more" className="border border-white/15 px-4 py-2 rounded-sm text-sm hover:bg-white/5">Load more</button></div>}
      </div>

      {active && <Detail cid={active} onClose={() => setActive(null)} onChanged={load} />}
      {showDupes && <DuplicatesModal onClose={() => setShowDupes(false)} onOpen={(cid) => { setShowDupes(false); setActive(cid); }} />}

      {showNew && (
        <div className="fixed inset-0 z-50 flex items-center justify-center" data-testid="contact-new-modal">
          <div className="absolute inset-0 bg-black/60" onClick={() => setShowNew(false)} />
          <div className="relative bg-[#0B0D12] border border-white/10 rounded-md p-6 w-full max-w-md">
            <div className="font-serif text-xl mb-4">New contact</div>
            <div className="space-y-3">
              <input value={nc.name} onChange={(e) => setNc({ ...nc, name: e.target.value })} placeholder="Name" className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm" data-testid="new-contact-name" />
              <input value={nc.email} onChange={(e) => setNc({ ...nc, email: e.target.value })} placeholder="Email" className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm" data-testid="new-contact-email" />
              <select value={nc.lifecycle_stage} onChange={(e) => setNc({ ...nc, lifecycle_stage: e.target.value })} className="w-full bg-white/5 border border-white/10 rounded-sm px-3 py-2 text-sm capitalize">
                {STAGES.map((s) => <option key={s} value={s}>{s}</option>)}
              </select>
            </div>
            <div className="flex gap-2 mt-5"><button onClick={createContact} data-testid="new-contact-create" className="bg-heraldic text-white px-4 py-2 rounded-sm text-sm">Create</button><button onClick={() => setShowNew(false)} className="border border-white/15 px-4 py-2 rounded-sm text-sm">Cancel</button></div>
          </div>
        </div>
      )}
    </AdminLayout>
  );
}
